import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { updatePassSettings } from '../../../../api/visitor-passes-client';
import { ConciergePassAccess } from '../../../../interfaces/visitor-pass';
import toast from '../../../../utils/toast';
import {
  UnitsSection,
  UnitsSectionHead,
  UnitsSectionNote,
  UnitsSectionTitle,
} from './dashboard-buildings.styled';

/**
 * VISITOR PASSES — what the front desk may do with them at ONE building
 * (migration 118, the owner's decision of 2026-09-28).
 *
 * Off / One-time visits only / All passes, default All. It saves the moment it
 * changes, through its own route (PUT /buildings/:id/visitor-passes), NOT the
 * building form's Save: that route is building:edit and multipart, and
 * property managers — who decide this as much as the owner does — hold
 * visitorpass:manage but not building:edit. So this section is editable
 * whenever the viewer holds visitorpass:manage, whether or not the rest of the
 * modal is in edit mode, and read-only (the same control, disabled) otherwise.
 *
 * The caller mounts it only for a real building id and visitorpass:read.
 */

const OPTIONS: ReadonlyArray<{ value: ConciergePassAccess; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'once', label: 'One-time only' },
  { value: 'all', label: 'All passes' },
];

const EXPLAIN: Record<ConciergePassAccess, string> = {
  off: 'The front desk sees who is expected and signs them in, but cannot add or change passes.',
  once: 'The front desk can add a one-time visit for today or tomorrow, and end one it added today. The household is told.',
  all: 'The front desk can add, change and end any visitor pass here, like a property manager. The household is told about every change.',
};

const known = (value: unknown): ConciergePassAccess =>
  value === 'off' || value === 'once' || value === 'all' ? value : 'all';

export interface BuildingPassesSectionProps {
  buildingId: number;
  buildingName: string;
  /** The building row's concierge_pass_access, as last read. */
  access: ConciergePassAccess | undefined;
  /** Called after a save, so the caller's building list can re-read. */
  onChanged?: (access: ConciergePassAccess) => void;
}

export const BuildingPassesSection: React.FC<BuildingPassesSectionProps> = ({
  buildingId,
  buildingName,
  access,
  onChanged,
}) => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canChange = can(permissions, PERMISSIONS.VISITORPASS_MANAGE);
  const [value, setValue] = useState<ConciergePassAccess>(known(access));
  const [saving, setSaving] = useState(false);
  const savedRef = useRef<ConciergePassAccess>(known(access));

  // A different building (or a re-read of this one) replaces what is shown.
  useEffect(() => {
    savedRef.current = known(access);
    setValue(known(access));
  }, [buildingId, access]);

  const choose = async (next: ConciergePassAccess) => {
    if (saving || next === savedRef.current) return;
    setValue(next);
    setSaving(true);
    try {
      const saved = await updatePassSettings(buildingId, { concierge_access: next });
      savedRef.current = known(saved.concierge_pass_access);
      setValue(savedRef.current);
      toast.success('Front desk access to visitor passes saved.');
      onChanged?.(savedRef.current);
    } catch {
      // What the server holds is what is shown: a failed save goes back.
      setValue(savedRef.current);
      toast.error('Could not save front desk access. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <UnitsSection aria-label={`Visitor passes in ${buildingName}`}>
      <UnitsSectionHead>
        <UnitsSectionTitle>Visitor passes</UnitsSectionTitle>
      </UnitsSectionHead>
      <SegmentedControl
        label="What the front desk can do with visitor passes"
        options={OPTIONS}
        value={value}
        onChange={next => {
          void choose(next);
        }}
        disabled={!canChange || saving}
      />
      <UnitsSectionNote>
        {EXPLAIN[value]}
        {canChange ? '' : ' Only an owner or a property manager can change this.'}
      </UnitsSectionNote>
    </UnitsSection>
  );
};
